import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search as SearchIcon, X, LayoutGrid, List, Users, Loader, ChevronRight, Check, BookOpen, Tablet, Plus, ArrowLeft, SlidersHorizontal, ChevronDown, Barcode } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../css/library.css';
import '../css/add_book.css';
import Scan from './scan';
import BookEditModal from './library_components/book_edit_modal';
import LibraryShareModal from './library_components/library_share_modal';
import BookDetailsModal from './library_components/book_details_modal';

function Library() {
  const [books, setBooks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [booksPerPage] = useState(102); // Set the number of books per page
  const [selectedBook, setSelectedBook] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [sortBy, setSortBy] = useState('added'); // Set default value to 'title'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchPlaceholder, setSearchPlaceholder] = useState('Search');
  const [isSearching, setIsSearching] = useState(false); // Add state for search button text
  const [addToCollectionMessage, setAddToCollectionMessage] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newUploadedImage, setNewUploadedImage] = useState('');
  const [readStateFilter, setReadStateFilter] = useState('');
  const [ownershipStateFilter, setOwnershipStateFilter] = useState('');
  const [ownerStateFilter, setOwnerStateFilter] = useState('');
  const [format, setFormat] = useState(false);
  const [special, setSpecial] = useState(false);
  const [loading, setLoading] = useState(false); // Introduce loading state
  const [showShareModal, setShowShareModal] = useState(false); // State for showing/hiding share modal
  const [sharedWith, setSharedWith] = useState([]);
  const [sharedBy, setSharedBy] = useState([]);
  const [newUser, setNewUser] = useState('');
  const [sharedWithInitialized, setSharedWithInitialized] = useState(false);
  const [sharedByInitialized, setSharedByInitialized] = useState(false);
  const [deletedBook, setDeletedBook] = useState(false);
  const [isCusomImageToggled, setIsCusomImageToggled] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanButtonText, setScanButtonText] = useState('Add Book');

  // Add Book view state
  const [addSearchTerm, setAddSearchTerm] = useState('');
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addIsbnLoading, setAddIsbnLoading] = useState(false);
  const [addSelectedBook, setAddSelectedBook] = useState(null);
  const [addSelectedIndex, setAddSelectedIndex] = useState(-1);
  const [addReadState, setAddReadState] = useState('UNREAD');
  const [addOwnershipState, setAddOwnershipState] = useState('OWNED');
  const [addFormat, setAddFormat] = useState(0);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addToast, setAddToast] = useState(null);
  const [addCustomImage, setAddCustomImage] = useState(null);
  const [scannerMode, setScannerMode] = useState(false);
  const addFileInputRef = useRef(null);
  const addSearchInputRef = useRef(null);
  const needsRefresh = useRef(false);
  const [bookCollections, setBookCollections] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const location = useLocation();
  const navigate = useNavigate();
  const [isRefreshingMetadata, setIsRefreshingMetadata] = useState(false);
  const [notifications, setNotifications] = useState([]);
  

  const isMobileDevice = () => {
    return window.innerWidth <= 768; // You can adjust the width as per your requirement
  }
  const [isMobile, setIsMobile] = useState(isMobileDevice());

  const STATUS_LABELS = {
    pending: 'Working',
    done: 'Complete',
    failed: 'Failed'
  };

  const parseImageLinks = (imageLinks) => {
    if (!imageLinks || typeof imageLinks !== 'string') {
      return {};
    }

    try {
      return JSON.parse(imageLinks);
    } catch (error) {
      return {};
    }
  };

  const hasBookThumbnail = (book) => {
    if (!book) {
      return false;
    }

    if (book.use_custom_image && book.uploaded_image_b64) {
      return true;
    }

    const imageLinks = parseImageLinks(book.imageLinks);
    return Boolean(imageLinks && imageLinks.thumbnail);
  };

  const dismissNotification = (id) => {
    setNotifications((prev) => prev.filter((note) => note.id !== id));
  };

  const addNotification = ({ title, subtitle, status = 'pending', statusText }) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const entry = {
      id,
      title,
      subtitle,
      status,
      statusText: statusText || STATUS_LABELS[status]
    };

    setNotifications((prev) => [entry, ...prev].slice(0, 4));
    return id;
  };

  const updateNotification = (id, patch, autoDismissDelay = 4500) => {
    setNotifications((prev) =>
      prev.map((note) => (note.id === id ? { ...note, ...patch } : note))
    );

    if (autoDismissDelay !== null) {
      setTimeout(() => {
        dismissNotification(id);
      }, autoDismissDelay);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Auto-open add-book view when navigated from sidebar shortcut
  useEffect(() => {
    if (location.state?.add && !isScanning) {
      setIsScanning(true);
      setScanButtonText('Back to Library');
      // Clear the state so refreshing doesn't re-trigger
      navigate('/library', { replace: true, state: {} });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the search input focused while scanner mode is active so USB
  // barcode scanners (HID keyboards) feed straight into the field.
  useEffect(() => {
    if (scannerMode && isScanning) {
      focusAddSearchInput();
    }
  }, [scannerMode, isScanning]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const modalOpen = Boolean(selectedBook || isEditModalOpen || showShareModal);
    document.body.classList.toggle('modal-open', modalOpen);
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [selectedBook, isEditModalOpen, showShareModal]);

  const renderMetaLine = (items) => {
    const filtered = items.filter((item) => item && item.value);
    if (filtered.length === 0) {
      return null;
    }

    const parts = [];
    filtered.forEach((item, index) => {
      parts.push(
        <span key={`${item.label}-${index}`} className="book-card-submeta-item">
          <span className="book-card-submeta-label">{item.label}:</span>
          {item.value}
        </span>
      );
      if (index < filtered.length - 1) {
        parts.push(
          <span key={`sep-${index}`} className="book-card-submeta-sep">|</span>
        );
      }
    });

    return <p className="book-card-submeta">{parts}</p>;
  };
  
  // Pagination Logic
  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = books.slice(indexOfFirstBook, indexOfLastBook);
  
  // Pagination Logic
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(books.length / booksPerPage); i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = () => {
    const maxVisiblePages = 3;
    const totalPages = pageNumbers.length;

    if (totalPages <= maxVisiblePages) {
      return pageNumbers.map(number => (
        <button
          key={number}
          onClick={() => setCurrentPage(number)}
          className={currentPage === number ? "active" : ""}
        >
          {number}
        </button>
      ));
    } else {
      let leftSide = [];
      let rightSide = [];

      if (currentPage <= maxVisiblePages - 1) {
        leftSide = pageNumbers.slice(0, maxVisiblePages);
        rightSide = ["right"];
      } else if (currentPage >= totalPages - maxVisiblePages + 1) {
        leftSide = ["left"];
        rightSide = pageNumbers.slice(totalPages - maxVisiblePages, totalPages);
      } else {
        const middleIndex = Math.max(currentPage - Math.floor(maxVisiblePages / 2), 0);
        leftSide = ["left"];
        rightSide = ["right"];

        for (let i = middleIndex; i < middleIndex + maxVisiblePages; i++) {
          leftSide.push(i);
        }
      }

      return (
        <>
          {leftSide.map((number, index) => (
            <button
              key={index}
              onClick={() => {
                if (number !== "left" && number !== "right") {
                  setCurrentPage(number);
                } else if (number === "left") {
                  setCurrentPage(currentPage - 1);
                }
                window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to the top of the page

              }}
              className={currentPage === number ? "active" : ""}
            >
              {number !== "left" && number !== "right" ? number : "←"}
            </button>
          ))}
          {rightSide.map((number, index) => (
            <button
              key={index + leftSide.length}
              onClick={() => {
                if (number !== "left" && number !== "right") {
                  setCurrentPage(number);
                } else if (number === "right") {
                  setCurrentPage(currentPage + 1);
                }
                window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to the top of the page
              }}
              className={currentPage === number ? "active" : ""}
            >
              {number !== "left" && number !== "right" ? number : "→"}
            </button>
          ))}
        </>
      );
    }
  };
      
  const getCollections = async () => {
    setLoading(true);
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');

    try {
      const response = await fetch("/api/library/collections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user, sortBy }),
      });
      if (response.ok) {
        const data = await response.json();
        setCollections(data);
      } else {
        console.error('Failed to fetch collections');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    getCollections();
    if (!sharedWithInitialized) {
      getSharedWith(); // Assuming this function is defined elsewhere
      setSharedWithInitialized(true);
    }
    if (!sharedByInitialized) {
      getSharedBy();
      setSharedByInitialized(true);
    }
  }, []);

  const getSharedWith = async () => {
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch("/api/auth/sharedwith", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user }),
      });
      if (response.ok) {
        const data = await response.json();
        setSharedWith(data);
      } else {
        console.error('Failed to fetch shared with');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getSharedBy = async () => {
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch("/api/auth/sharedby", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user }),
      });
      if (response.ok) {
        const data = await response.json();
        setSharedBy(data);
      } else {
        console.error('Failed to fetch shared by');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const shareBooks = async () => {
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch("/api/auth/share", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user, newUser }),
      });
      if (response.ok) {
        // Optionally, you may want to update the UI or perform any necessary actions
        getSharedWith();
        setNewUser(''); // Clear input field after sharing
      } else {
        console.error('Failed to share books');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const unshareUser = async (unshareUser) => {
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch("/api/auth/unshare", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user, unshareUser }),
      });
      if (response.ok) {
        getSharedWith();
        setNewUser('');
      } else {
        console.error('Failed to share books');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCollectionSelect = (e) => {
    setSelectedCollectionId(e.target.value);
  };

  const handleAddToCollection = async () => {
    const collectionId = selectedCollectionId || collections[0].id;
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');

    if (!selectedBook) return;
    try {
      const response = await fetch("/api/library/collection/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          collectionId,
          bookId: selectedBook.id,
        }),
      });
      if (response.ok) {
        setAddToCollectionMessage('Book added to collection successfully.');
        // Optionally, you may want to update the UI or perform any necessary actions
      } else {
        setAddToCollectionMessage('Failed to add book to collection.');
        console.error('Failed to add book to collection');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const READ_STATE = {
    0: "READ",
    1: "UNREAD",
    2: "READING"
  };
  
  const OWNERSHIP_STATE = {
    0: "OWNED",
    1: "ORDERED",
    2: "WANTED"
  };

  const getStatusStyle = (value) => {
    switch(value) {
      case READ_STATE[0]:
        return { backgroundColor: 'var(--color-status-read-bg)', color: 'var(--color-status-read)' };
      case READ_STATE[1]:
        return { backgroundColor: 'var(--color-status-unread-bg)', color: 'var(--color-status-unread)' };
      case READ_STATE[2]:
        return { backgroundColor: 'var(--color-status-reading-bg)', color: 'var(--color-status-reading)' };
      case OWNERSHIP_STATE[0]:
        return { backgroundColor: 'var(--color-status-owned-bg)', color: 'var(--color-status-owned)' };
      case OWNERSHIP_STATE[1]:
        return { backgroundColor: 'var(--color-status-ordered-bg)', color: 'var(--color-status-ordered)' };
      case OWNERSHIP_STATE[2]:
        return { backgroundColor: 'var(--color-status-wanted-bg)', color: 'var(--color-status-wanted)' };
      default:
        return { backgroundColor: 'var(--color-status-reading-bg)', color: 'var(--color-status-reading)' };
    }
  };

  const getAllBooks = async () => {
    setLoading(true);
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');
    try {
      const response = await fetch("/api/library/books", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user, sortBy, readState: readStateFilter, ownershipState: ownershipStateFilter, ownedState: ownerStateFilter }),
      });
      if (response.ok) {
        const data = await response.json();
        setBooks(data);
      } else {
        console.error('Login or registration failed');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sharedByInitialized) {
      getAllBooks();
    }
  }, [sortBy, readStateFilter, ownershipStateFilter, ownerStateFilter, sharedBy, sharedByInitialized]);

  const handleSearch = async () => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) {
      setSearchTerm('');
      setSearchPlaceholder('No search provided');
      setIsSearching(false);
      return;
    }
    const user = localStorage.getItem('loggedInUsername');
    const token = localStorage.getItem('accessToken');

    try {
      const response = await fetch("/api/library/search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ searchTerm: trimmedSearch, user }),
      });
      if (response.ok) {
        const data = await response.json();
        setBooks(data);
      } else {
        console.error('Search failed');
      }
       
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleClearSearch = async() => {
    setIsSearching(false);
    setSearchTerm('');
    getAllBooks()
  }

  // Function to clear filters
  const clearFilters = () => {
    setOwnershipStateFilter('');
    setReadStateFilter('');
    setOwnerStateFilter('');
    setSortBy('added');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      document.getElementById('searchButton').click();
    }
  };

  const getBookCollections = async (book) => {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');

    try {
      const response = await fetch("/api/library/book/collections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          user: user,
          book: book.isbn
        })
      });
      if (response.ok) {
        const data = await response.json()
        setBookCollections(data.collection_names)
      } else {
        console.error('Failed to get book collections');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };


  const handleBookClick = (book) => {
    getBookCollections(book);
    setSelectedBook(book);
    setIsCusomImageToggled(book.use_custom_image);
  };

  const handleCloseBookDetails = () => {
    setAddToCollectionMessage('');
    setSelectedBook(null);
    if (needsRefresh.current) {
      needsRefresh.current = false;
      if (searchTerm.length > 0) {
        handleSearch();
      } else {
        getAllBooks();
      }
    }
  };

  const handleReadStateChange = (e) => {
    const newReadState = e.target.value;
    setSelectedBook(prevBook => ({
      ...prevBook,
      read: newReadState
    }));
    updateBook({ ...selectedBook, read: newReadState });
  };
  
  const handleOwnershipStateChange = (e) => {
    const newOwnershipState = e.target.value;
    setSelectedBook(prevBook => ({
      ...prevBook,
      ownership: newOwnershipState
    }));
    updateBook({ ...selectedBook, ownership: newOwnershipState });
  };

  const updateBook = async (updatedBook) => {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');

    try {
      const response = await fetch("/api/library/modify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          user: user,
          isbn: updatedBook.isbn, 
          readState: updatedBook.read,
          ownershipState: updatedBook.ownership,
          rating: updatedBook.rating, // include rating in the request body
          format: updatedBook.format,
          special: updatedBook.special
        }),
      });
      if (response.ok) {
        needsRefresh.current = true;
      } else {
        console.error('Failed to update book');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };



  const convertTimestampToDate = (timestamp) => {
    // Multiply by 1000 if the timestamp is in seconds
    const date = new Date(timestamp * 1000);
    // Format the date as needed (e.g., "MM/DD/YYYY hh:mm:ss")
    const formattedDate = date.toLocaleString(); // Adjust the formatting as per your requirement
    return formattedDate;
  };

  // Function to open the edit modal
  const handleEditClick = () => {
    setIsEditModalOpen(true);
    setNewTitle(selectedBook.title); // Set default title value
    setNewDescription(selectedBook.description); // Set default description value
    setIsCusomImageToggled(selectedBook.use_custom_image);
  };
  
  const handleFormatClick = (book) => {
    const newFormat = !book.format;
    setFormat(newFormat);
    setSelectedBook(prevBook => ({
      ...prevBook,
      format: newFormat
    }));
    updateBook({ ...selectedBook, format: newFormat });
  };

  const handleSpecialClick = (book) => {
    const newSpecial = !book.special;
    setSpecial(newSpecial);
    setSelectedBook(prevBook => ({
      ...prevBook,
      special: newSpecial
    }));
    updateBook({ ...selectedBook, special: newSpecial });
  }

  // Function to close the edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    
    // Create a new image element
    const img = new Image();
  
    // Define a function to handle loading of the image
    img.onload = function () {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
  
      // Calculate new dimensions
      let width = img.width;
      let height = img.height;
      const aspectRatio = width / height;
      const targetWidth = 128;
      const targetHeight = 196;
  
      if (width > height) {
        width = targetWidth;
        height = width / aspectRatio;
      } else {
        height = targetHeight;
        width = height * aspectRatio;
      }
  
      // Set canvas dimensions
      canvas.width = targetWidth;
      canvas.height = targetHeight;
  
      // Draw image on canvas
      ctx.drawImage(img, 0, 0, width, height);
  
      // Get base64 representation of the resized image
      const resizedImageData = canvas.toDataURL('image/jpeg');
  
      // Update state with the base64-encoded image data
      setNewUploadedImage(resizedImageData);
    };
  
    // Read the file as a data URL
    img.src = URL.createObjectURL(file);
  };
  
  const handleIsCustomImageToggled = () => {
    setIsCusomImageToggled(!isCusomImageToggled);
  }
  

  // Function to handle saving the new title
  const handleSaveTitle = async () => {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');

    try {
      const response = await fetch("/api/library/book/modify-title", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          user: user,
          bookId: selectedBook.id,
          newTitle: newTitle,
          newDescription: newDescription,
          useCustomImage: isCusomImageToggled,
          newUploadedImage: newUploadedImage
        }),
      });
      if (response.ok) {
        // Close the edit modal and update book in state
        handleCloseEditModal();
        setSelectedBook(prevBook => ({
          ...prevBook,
          title: newTitle,
          description: newDescription,
          use_custom_image: isCusomImageToggled,
          uploaded_image_b64: newUploadedImage
        }));
        needsRefresh.current = true;
        
      } else {
        console.error('Failed to modify title');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleRefreshMetadata = async () => {
    if (!selectedBook) return;
    setIsRefreshingMetadata(true);
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');
    const hadThumbnail = hasBookThumbnail(selectedBook);
    const taskId = addNotification({
      title: 'Refreshing metadata',
      subtitle: selectedBook.title,
      status: 'pending',
      statusText: 'Working'
    });

    try {
      const response = await fetch("/api/library/book/refresh-metadata", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          user: user,
          bookId: selectedBook.id
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedBook(data);
        updateNotification(taskId, { status: 'done', statusText: 'Updated' });
        if (!hadThumbnail && hasBookThumbnail(data)) {
          addNotification({
            title: 'Cover image fetched',
            subtitle: data.title,
            status: 'done',
            statusText: 'Ready'
          });
        }
        needsRefresh.current = true;
      } else {
        console.error('Failed to refresh metadata');
        updateNotification(taskId, { status: 'failed', statusText: 'Failed' }, 6500);
      }
    } catch (error) {
      console.error('Error:', error);
      updateNotification(taskId, { status: 'failed', statusText: 'Failed' }, 6500);
    } finally {
      setIsRefreshingMetadata(false);
    }
  };

  const deleteBook = async (bookId) => {
    setSelectedBook(null);
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');

    try {
      const response = await fetch("/api/library/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ bookId: bookId, user: user }),
      });
      if (response.ok) {
        // Collection deleted successfully, update UI or perform any necessary actions
        if(searchTerm.length > 0){
          handleSearch();
        }else{
          getAllBooks();
        }
      } else {
        console.error('Failed to delete collection');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const toggleIsScanning = () => {
    if(!isScanning){
      setScanButtonText('Back to Library');
    }
    else{
      setScanButtonText('Add Book');
      getAllBooks();
      // Reset add book state
      setAddSelectedBook(null);
      setAddSelectedIndex(-1);
      setAddSearchResults([]);
      setAddSearchTerm('');
      setAddToast(null);
      setAddCustomImage(null);
      setScannerMode(false);
    }
    setIsScanning(!isScanning);
  }

  // ── Add Book: refresh library after a book is added ──
  const refreshAfterAdd = () => {
    needsRefresh.current = true;
    getAllBooks();
  };

  const isIsbnLike = (value) => {
    const digits = (value || '').replace(/[-\s]/g, '');
    return /^\d{9}[\dXx]$/.test(digits) || /^\d{13}$/.test(digits);
  };

  const focusAddSearchInput = () => {
    if (addSearchInputRef.current) {
      addSearchInputRef.current.focus();
    }
  };

  // ── Add Book: unified submit (search or ISBN) ──
  const handleAddSubmit = () => {
    if (!addSearchTerm.trim()) return;
    if (isIsbnLike(addSearchTerm)) {
      handleAddIsbn();
    } else {
      handleAddSearch();
    }
  };

  // ── Add Book: Google search ──
  const handleAddSearch = async () => {
    if (!addSearchTerm.trim()) return;
    setAddSearching(true);
    setAddSearchResults([]);
    setAddSelectedBook(null);
    setAddSelectedIndex(-1);
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');
    try {
      const response = await fetch('/api/library/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ q: addSearchTerm, owner: user }),
      });
      if (response.ok) {
        const data = await response.json();
        setAddSearchResults(data);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setAddSearching(false);
    }
  };

  // ── Add Book: ISBN submit ──
  const handleAddIsbn = async () => {
    const isbn = addSearchTerm.replace(/[-\s]/g, '');
    if (!isbn) return;
    setAddIsbnLoading(true);
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');
    try {
      const response = await fetch('/api/library/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isbn, owner: user, readState: addReadState, ownershipState: addOwnershipState, timestamp: Math.floor(Date.now() / 1000), format: addFormat }),
      });
      const data = await response.json();
      if (response.ok) {
        refreshAfterAdd();
        setAddSearchTerm('');
        if (scannerMode) focusAddSearchInput();
      } else {
        showAddToast('error', data.error || 'Failed to add book');
        if (scannerMode) { setAddSearchTerm(''); focusAddSearchInput(); }
      }
    } catch (err) {
      showAddToast('error', 'Network error');
    } finally {
      setAddIsbnLoading(false);
    }
  };

  // ── Add Book: add selected search result ──
  const handleAddSelectedBook = async () => {
    if (!addSelectedBook) return;
    setAddSubmitting(true);
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('loggedInUsername');
    try {
      const response = await fetch('/api/library/add-searched-book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user, book: addSelectedBook, readState: addReadState, ownershipState: addOwnershipState, timestamp: Math.floor(Date.now() / 1000), format: addFormat }),
      });
      if (response.ok) {
        refreshAfterAdd();
        setAddSearchResults(prev => prev.map((b, i) => i === addSelectedIndex ? { ...b, found: true } : b));
        setAddSelectedBook(null);
        setAddSelectedIndex(-1);
        setAddCustomImage(null);
      } else {
        const data = await response.json();
        showAddToast('error', data.error || 'Failed to add');
      }
    } catch (err) {
      showAddToast('error', 'Network error');
    } finally {
      setAddSubmitting(false);
    }
  };

  // ── Add Book: book added via scan ──
  const handleScanAdded = (book) => {
    refreshAfterAdd();
  };

  const showAddToast = (type, message) => {
    setAddToast({ type, message });
    setTimeout(() => setAddToast(null), 4000);
  };

  const getAddBookThumbnail = (book) => {
    if (addCustomImage) return addCustomImage;
    try {
      const links = typeof book.imageLinks === 'string' ? JSON.parse(book.imageLinks) : book.imageLinks;
      return links?.thumbnail || null;
    } catch { return null; }
  };

  // ── Add Book: derive recently-added log from the user's whole library ──
  const recentBooks = useMemo(() => {
    return [...books]
      .filter((b) => b && b.title)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      .map((b, i) => {
        let thumbnail = null;
        if (b.use_custom_image && b.uploaded_image_b64) {
          thumbnail = b.uploaded_image_b64;
        } else {
          const links = parseImageLinks(b.imageLinks);
          thumbnail = links?.thumbnail || null;
        }
        return {
          id: b.id ?? `${b.isbn || b.title}-${b.timestamp || i}`,
          title: b.title,
          author: b.author || '',
          thumbnail
        };
      });
  }, [books]);

  const handleAddImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 196;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 128, 196);
        setAddCustomImage(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };
  
  return (
    <div className='lib-page library-page'>
      {/* ── Page Header ── */}
      <div className="library-header">
        <div className="library-header-left">
          {isScanning ? (
            <>
              <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }} onClick={() => toggleIsScanning()}>
                <ArrowLeft size={20} style={{ color: 'var(--color-text-muted)' }} />
                Add to Library
              </h1>
            </>
          ) : (
            <>
              <h1>My Library</h1>
              <p className="library-header-subtitle">
                {books.length} book{books.length !== 1 ? 's' : ''} in your collection
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── Search Toolbar ── */}
      {!isScanning && (
        <div className="search-toolbar">
          <div className="search-toolbar-row">
            <button id="searchButton" className="btn-icon" onClick={() => { setCurrentPage(1); setIsSearching(true); handleSearch(); }} title="Search">
              <SearchIcon size={16} />
            </button>
            <input
              id="search-box"
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => {
                if (searchPlaceholder !== 'Search') {
                  setSearchPlaceholder('Search');
                }
                setSearchTerm(e.target.value);
              }}
              onFocus={() => {
                if (searchPlaceholder !== 'Search') {
                  setSearchPlaceholder('Search');
                }
              }}
              onKeyDown={handleKeyDown}
              className='search-box'
            />
            <div className="pagination search-pagination">
              {renderPageNumbers()}
            </div>
            {isSearching && (
              <button id="clearSearchButton" className="btn" onClick={() => { handleClearSearch(); }}>
                <X size={14} /> Clear
              </button>
            )}
            <button className="btn btn-primary mobile-hide" onClick={() => toggleIsScanning()}>
              <Plus size={14} /> Add Book
            </button>
            <button className="btn mobile-filter-toggle" onClick={() => setShowMobileFilters(f => !f)}>
              <SlidersHorizontal size={14} />
              <span>Filters</span>
              <ChevronDown size={12} className={`mobile-filter-chevron${showMobileFilters ? ' open' : ''}`} />
            </button>
          </div>
        </div>
      )}

      {notifications.length > 0 && (
        <aside className="task-notifications" aria-live="polite">
          <div className="task-notifications-header">
            <span>Background tasks</span>
            <button
              className="task-notifications-clear"
              onClick={() => setNotifications([])}
              type="button"
            >
              Clear
            </button>
          </div>
          <ul className="task-notifications-list">
            {notifications.map((note) => (
              <li key={note.id} className={`task-note task-note-${note.status}`}>
                <div className="task-note-text">
                  <span className="task-note-title">{note.title}</span>
                  {note.subtitle && (
                    <span className="task-note-sub">{note.subtitle}</span>
                  )}
                </div>
                <div className="task-note-meta">
                  {note.status === 'pending' && (
                    <span className="task-note-pulse" aria-hidden="true" />
                  )}
                  <span className="task-note-status">
                    {note.statusText || STATUS_LABELS[note.status]}
                  </span>
                  <button
                    className="task-note-dismiss"
                    onClick={() => dismissNotification(note.id)}
                    type="button"
                    aria-label="Dismiss notification"
                  >
                    x
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </aside>
      )}

      {isScanning ? (
        <div className="add-book-view">
          <div className="add-book-columns">
            {/* Left Column — Find a book (scan + search/ISBN + results) */}
            <div className="add-book-left">
              <div className="add-book-scan-section">
                <Scan onBookAdded={handleScanAdded} onError={(msg) => showAddToast('error', msg)} />
              </div>

              <div className="add-book-divider">
                <span>or enter manually</span>
              </div>

              <button
                type="button"
                className={`add-book-scanner-toggle${scannerMode ? ' active' : ''}`}
                onClick={() => setScannerMode((v) => !v)}
                aria-pressed={scannerMode}
              >
                <span className="add-book-scanner-toggle-label">
                  <Barcode size={15} />
                  USB barcode scanner
                </span>
                <span className="add-book-scanner-switch" aria-hidden="true">
                  <span className="add-book-scanner-knob" />
                </span>
              </button>

              <div className={`add-book-search-box${scannerMode ? ' scanner-active' : ''}`}>
                {scannerMode ? <Barcode size={15} /> : <SearchIcon size={15} />}
                <input
                  ref={addSearchInputRef}
                  type="text"
                  placeholder={scannerMode ? 'Scan a barcode\u2026' : 'Search by title, author, or ISBN'}
                  value={addSearchTerm}
                  onChange={(e) => setAddSearchTerm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubmit(); } }}
                  onBlur={() => { if (scannerMode) setTimeout(focusAddSearchInput, 0); }}
                />
                {isIsbnLike(addSearchTerm) && <span className="add-book-isbn-hint">ISBN</span>}
                {!scannerMode && (
                  <button
                    className="add-book-search-submit"
                    onClick={handleAddSubmit}
                    disabled={(addSearching || addIsbnLoading) || !addSearchTerm.trim()}
                  >
                    {isIsbnLike(addSearchTerm)
                      ? (addIsbnLoading ? 'Adding' : 'Add')
                      : (addSearching ? 'Searching' : 'Search')}
                  </button>
                )}
                {scannerMode && addIsbnLoading && (
                  <Loader size={15} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
                )}
              </div>

              {scannerMode && (
                <p className="add-book-scanner-hint">
                  Scanner ready &mdash; scanned ISBNs are added automatically and the field clears for the next scan.
                </p>
              )}

              <div className="add-book-results">
                {addSearching && (
                  <div className="add-book-searching">
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Searching
                  </div>
                )}
                {!addSearching && addSearchResults.length === 0 && addSearchTerm && !isIsbnLike(addSearchTerm) && (
                  <div className="add-book-no-results">No results found</div>
                )}
                {addSearchResults.map((book, index) => {
                  const thumb = getAddBookThumbnail(book);
                  return (
                    <div
                      key={index}
                      className={`add-book-result${addSelectedIndex === index ? ' selected' : ''}${book.found ? ' in-library' : ''}`}
                      onClick={() => !book.found && (setAddSelectedBook(book), setAddSelectedIndex(index), setAddToast(null), setAddCustomImage(null))}
                    >
                      {thumb ? (
                        <img className="add-book-result-thumb" src={thumb} alt="" />
                      ) : (
                        <div className="add-book-result-thumb" />
                      )}
                      <div className="add-book-result-info">
                        <div className="add-book-result-title">{book.title}</div>
                        <div className="add-book-result-meta">
                          {book.author}{book.publishedDate ? ` \u00B7 ${book.publishedDate}` : ''}
                        </div>
                      </div>
                      {book.found ? (
                        <div className="add-book-in-library-badge">
                          <Check size={10} /> Added
                        </div>
                      ) : addSelectedIndex === index ? (
                        <ChevronRight size={14} className="add-book-result-arrow" />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Middle Column — Preview */}
            <div className="add-book-right">
              {addSelectedBook ? (
                <div className="add-book-preview-card">
                  <div className="add-book-preview-header">
                    <div className="add-book-cover-area">
                      {getAddBookThumbnail(addSelectedBook) ? (
                        <img className="add-book-cover-img" src={getAddBookThumbnail(addSelectedBook)} alt="" />
                      ) : (
                        <div className="add-book-cover-placeholder">
                          <BookOpen size={32} />
                        </div>
                      )}
                      <input ref={addFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAddImageUpload} />
                      <button className="add-book-upload-link" onClick={() => addFileInputRef.current?.click()}>Upload cover</button>
                    </div>
                    <div className="add-book-book-meta">
                      <h2 className="add-book-book-title">{addSelectedBook.title}</h2>
                      <p className="add-book-book-author">{addSelectedBook.author}</p>
                      <div className="add-book-meta-row">
                        {addSelectedBook.publishedDate && <span>{addSelectedBook.publishedDate}</span>}
                        {addSelectedBook.pageCount && <><span>\u00B7</span><span>{addSelectedBook.pageCount} pages</span></>}
                        {addSelectedBook.isbn && <><span>\u00B7</span><span>ISBN {addSelectedBook.isbn}</span></>}
                      </div>
                      {addSelectedBook.description && (
                        <p className="add-book-description">{addSelectedBook.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="add-book-separator" />

                  <div className="add-book-form">
                    <div className="add-book-field">
                      <span className="add-book-field-label">Status</span>
                      <div className="add-book-pills">
                        {[['UNREAD', 'Unread'], ['READ', 'Read'], ['READING', 'Reading']].map(([val, label]) => (
                          <button key={val} className={`add-book-pill${addReadState === val ? ' active' : ''}`} onClick={() => setAddReadState(val)}>{label}</button>
                        ))}
                      </div>
                    </div>
                    <div className="add-book-field">
                      <span className="add-book-field-label">Ownership</span>
                      <div className="add-book-pills">
                        {[['OWNED', 'Owned'], ['ORDERED', 'Ordered'], ['WANTED', 'Wanted']].map(([val, label]) => (
                          <button key={val} className={`add-book-pill${addOwnershipState === val ? ' active' : ''}`} onClick={() => setAddOwnershipState(val)}>{label}</button>
                        ))}
                      </div>
                    </div>
                    <div className="add-book-field">
                      <span className="add-book-field-label">Format</span>
                      <div className="add-book-pills">
                        <button className={`add-book-pill${addFormat === 0 ? ' active' : ''}`} onClick={() => setAddFormat(0)}>
                          <BookOpen size={12} /> Physical
                        </button>
                        <button className={`add-book-pill${addFormat === 1 ? ' active' : ''}`} onClick={() => setAddFormat(1)}>
                          <Tablet size={12} /> Digital
                        </button>
                      </div>
                    </div>

                    <button
                      className="add-book-submit-btn"
                      onClick={handleAddSelectedBook}
                      disabled={addSubmitting || addSelectedBook.found}
                    >
                      {addSubmitting ? (
                        <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Adding</>
                      ) : addSelectedBook.found ? (
                        <><Check size={16} /> Already in Library</>
                      ) : (
                        <><Plus size={16} /> Add to Library</>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="add-book-recent">
                  <div className="add-book-recent-header">
                    <h2>Recently Added</h2>
                    <span>{recentBooks.length} book{recentBooks.length !== 1 ? 's' : ''} in your library</span>
                  </div>
                  <div className="add-book-recent-list">
                    {recentBooks.length === 0 ? (
                      <div className="add-book-recent-empty">
                        <BookOpen size={48} />
                        <p>Scan, search, or enter an ISBN to add a book. The books you add will appear here.</p>
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const [featured, ...queue] = recentBooks;
                          return (
                            <>
                              <div className="add-book-recent-featured">
                                {featured.thumbnail ? (
                                  <img className="add-book-recent-featured-thumb" src={featured.thumbnail} alt="" />
                                ) : (
                                  <div className="add-book-recent-featured-thumb">
                                    <BookOpen size={32} />
                                  </div>
                                )}
                                <div className="add-book-recent-featured-info">
                                  <div className="add-book-recent-featured-badge">
                                    <Check size={12} /> Latest addition
                                  </div>
                                  <div className="add-book-recent-featured-title">{featured.title}</div>
                                  {featured.author && <div className="add-book-recent-featured-author">{featured.author}</div>}
                                </div>
                              </div>

                              {queue.length > 0 && (
                                <div className="add-book-recent-queue">
                                  {queue.map((entry) => (
                                    <div key={entry.id} className="add-book-recent-item">
                                      {entry.thumbnail ? (
                                        <img className="add-book-recent-thumb" src={entry.thumbnail} alt="" />
                                      ) : (
                                        <div className="add-book-recent-thumb" />
                                      )}
                                      <div className="add-book-recent-info">
                                        <div className="add-book-recent-title">{entry.title}</div>
                                        {entry.author && <div className="add-book-recent-author">{entry.author}</div>}
                                      </div>
                                      <Check size={15} className="add-book-recent-check" />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {addToast && (
            <div className={`add-book-toast${addToast.type === 'error' ? ' error' : ''}`}>
              {addToast.type === 'success' && <Check size={14} />}
              {addToast.message}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={`filter-bar${showMobileFilters ? ' mobile-open' : ''}`}>
            <div className="filter-segment">
              <span className="filter-segment-label">Sort</span>
              {[['added', 'Added'], ['title', 'Title'], ['author', 'Author']].map(([val, label]) => (
                <button key={val} className={`filter-pill${sortBy === val ? ' active' : ''}`} onClick={() => setSortBy(val)}>{label}</button>
              ))}
            </div>

            <div className="filter-divider" />

            <div className="filter-segment">
              <span className="filter-segment-label">Status</span>
              {[['', 'All'], ['READ', 'Read'], ['UNREAD', 'Unread'], ['READING', 'Reading']].map(([val, label]) => (
                <button key={val} className={`filter-pill${readStateFilter === val ? ' active' : ''}`} onClick={() => setReadStateFilter(val)}>{label}</button>
              ))}
            </div>

            <div className="filter-divider" />

            <div className="filter-segment">
              <span className="filter-segment-label">Ownership</span>
              {[['', 'All'], ['OWNED', 'Owned'], ['ORDERED', 'Ordered'], ['WANTED', 'Wanted']].map(([val, label]) => (
                <button key={val} className={`filter-pill${ownershipStateFilter === val ? ' active' : ''}`} onClick={() => setOwnershipStateFilter(val)}>{label}</button>
              ))}
            </div>

            {(sharedBy.length > 0 && sharedBy[sharedBy.length - 1].name.length > 0) && (
              <>
                <div className="filter-divider" />
                <div className="filter-segment">
                  <span className="filter-segment-label">Owner</span>
                  <button className={`filter-pill${ownerStateFilter === '' ? ' active' : ''}`} onClick={() => setOwnerStateFilter('')}>All</button>
                  <button className={`filter-pill${ownerStateFilter === localStorage.getItem('loggedInUsername') ? ' active' : ''}`} onClick={() => setOwnerStateFilter(localStorage.getItem('loggedInUsername'))}>{localStorage.getItem('loggedInUsername')}</button>
                  {sharedBy.map((user, index) => (
                    <button key={index} className={`filter-pill${ownerStateFilter === user.name ? ' active' : ''}`} onClick={() => setOwnerStateFilter(user.name)}>{user.name}</button>
                  ))}
                </div>
              </>
            )}

            <div className="filter-divider" />

            <div className="filter-segment">
              <span className="filter-segment-label">View</span>
              <button className={`filter-pill${viewMode === 'grid' ? ' active' : ''}`} onClick={() => setViewMode('grid')}><LayoutGrid size={12} /> Grid</button>
              <button className={`filter-pill${viewMode === 'list' ? ' active' : ''}`} onClick={() => setViewMode('list')}><List size={12} /> List</button>
            </div>

            <div className="filter-bar-spacer" />

            <div className="filter-bar-actions">
              <button className="filter-bar-btn" onClick={() => setShowShareModal(true)}>
                <Users size={13} /> Share
              </button>
              <button className="filter-bar-btn" onClick={clearFilters}>
                <X size={13} /> Clear
              </button>
            </div>
          </div>
          
          {loading && (
            <div className="loading-container">
              <Loader size={32} className="loading-spinner" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )} {/* Display loading message when loading is true */}

          <table className={`custom-table view-${viewMode}`}>
            <tbody>
              <tr>
                {currentBooks.map((book, index) => {
                  // Parse the imageLinks string into a JSON object
                  const imageLinks = JSON.parse(book.imageLinks);

                  return (
                    <td className={book.special ? "special-book-item" : "book-item"} key={index}>
                      <div className="book-card" onClick={() => handleBookClick(book)}>
                        <div className="book-card-media">
                          { (book.use_custom_image && book.uploaded_image_b64 != "") ? (
                            <img className="book-thumbnail" src={book.uploaded_image_b64} alt={book.title} />
                          ) : imageLinks && imageLinks.thumbnail ? (
                            <img className="book-thumbnail" src={imageLinks.thumbnail} alt={book.title} />
                          ) : null}
                        </div>
                        <div className="book-card-body">
                          <p className="book-card-title" title={book.title}>{book.title}</p>
                          {viewMode === 'list' && (
                            <div className="book-card-details">
                              {book.author && (
                                <p className="book-card-author">{book.author}</p>
                              )}
                              {renderMetaLine([
                                { label: 'Publisher', value: book.publisher },
                                { label: 'Published', value: book.publishedDate }
                              ])}
                              {renderMetaLine([
                                { label: 'ISBN', value: book.isbn },
                                { label: 'Owner', value: book.owner }
                              ])}
                              {renderMetaLine([
                                { label: 'Added', value: book.timestamp ? convertTimestampToDate(book.timestamp) : '' }
                              ])}
                            </div>
                          )}
                          <div className="book-card-meta">
                            <span
                              className="status-chip"
                              style={{
                                ...getStatusStyle(book.ownership)
                              }}
                            >
                              {book.ownership}
                            </span>
                            <span
                              className="status-chip"
                              style={{
                                ...getStatusStyle(book.read)
                              }}
                            >
                              {book.read}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>

          <div className="pagination">
            {renderPageNumbers()}
          </div>


          <BookDetailsModal
            selectedBook={selectedBook}
            handleEditClick={handleEditClick}
            deleteBook={deleteBook}
            format={format}
            handleFormatClick={handleFormatClick}
            special={special}
            handleSpecialClick={handleSpecialClick}
            convertTimestampToDate={convertTimestampToDate}
            collections={collections}
            selectedCollectionId={selectedCollectionId}
            handleCollectionSelect={handleCollectionSelect}
            handleAddToCollection={handleAddToCollection}
            addToCollectionMessage={addToCollectionMessage}
            bookCollections={bookCollections}
            READ_STATE={READ_STATE}
            OWNERSHIP_STATE={OWNERSHIP_STATE}
            handleReadStateChange={handleReadStateChange}
            handleOwnershipStateChange={handleOwnershipStateChange}
            handleCloseBookDetails={handleCloseBookDetails}
            handleRefreshMetadata={handleRefreshMetadata}
            isRefreshingMetadata={isRefreshingMetadata}
          />

          <BookEditModal 
            isEditModalOpen={isEditModalOpen}
            selectedBook={selectedBook}
            newTitle={newTitle}
            setNewTitle={setNewTitle}
            newDescription={newDescription}
            setNewDescription={setNewDescription}
            isCusomImageToggled={isCusomImageToggled}
            handleIsCustomImageToggled={handleIsCustomImageToggled}
            handleImageUpload={handleImageUpload}
            handleSaveTitle={handleSaveTitle}
            handleCloseEditModal={handleCloseEditModal}
          />

          <LibraryShareModal 
            showShareModal={showShareModal}
            sharedWith={sharedWith}
            newUser={newUser}
            setNewUser={setNewUser}
            unshareUser={unshareUser}
            shareBooks={shareBooks}
            setShowShareModal={setShowShareModal}
          />
        </>
      )}
    </div>
  );
}

export default Library;
